<template>
  <div class="flex flex-col p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
    <div class="flex items-center gap-2">
      <button
        v-if="isMultiTenant"
        ref="triggerButtonRef"
        type="button"
        class="flex-shrink-0 w-10 h-10 rounded-xl border-2 border-gray-300 dark:border-gray-600 hover:border-indigo-500 dark:hover:border-indigo-400 text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center justify-center transition-colors"
        title="Select tenant"
        @click="handleToggleTenantSelector"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
      </button>
      <div
        :class="[
          'flex flex-1 items-center rounded-xl border overflow-hidden',
          isTooLong
            ? 'border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/20 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500'
            : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500',
        ]"
      >
        <textarea
          ref="inputRef"
          v-model="message"
          rows="1"
          :disabled="disabled"
          :placeholder="placeholder"
          class="flex-1 resize-none border-none bg-transparent py-2.5 pl-4 pr-1 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-0 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed max-h-32 overflow-y-auto"
          @keydown.enter.exact.prevent="handleSend"
          @input="handleInput"
        />
        <button
          v-if="isSupported"
          type="button"
          :disabled="disabled || isLoading"
          :aria-label="isListening ? 'Stop recording' : 'Start voice input'"
          :title="isListening ? 'Stop recording' : 'Start voice input'"
          :class="[
            'flex-shrink-0 px-2 self-center transition-colors focus:outline-none',
            isListening
              ? 'text-red-500 animate-pulse'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100',
            (disabled || isLoading) && 'opacity-50 cursor-not-allowed',
          ]"
          @click="toggleSpeechRecognition"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z" />
            <path
              d="M17 11a1 1 0 0 1 2 0 7 7 0 0 1-6 6.92V20h3a1 1 0 1 1 0 2H8a1 1 0 1 1 0-2h3v-2.08A7 7 0 0 1 5 11a1 1 0 1 1 2 0 5 5 0 0 0 10 0Z"
            />
          </svg>
        </button>
      </div>
      <button
        :disabled="!canSend"
        class="flex-shrink-0 w-10 h-10 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
        @click="handleSend"
      >
        <svg
          v-if="!isLoading"
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"
          />
        </svg>
        <svg v-else class="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </button>
    </div>

    <div
      v-if="message.length > 0"
      class="text-xs mt-1 text-right"
      :class="isTooLong ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'"
    >
      <span v-if="isTooLong">
        Message too long ({{ message.length.toLocaleString() }} / {{ MAX_MESSAGE_LENGTH.toLocaleString() }} characters)
      </span>
      <span v-else-if="remainingChars < 1000"> {{ remainingChars.toLocaleString() }} characters remaining </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue';
import { useSpeechRecognition } from '@/composables/use-speech-recognition';

const props = defineProps<{
  disabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  isMultiTenant?: boolean;
}>();

const emit = defineEmits<{
  (e: 'send', message: string): void;
  (e: 'toggle-tenant-selector'): void;
}>();

const MAX_MESSAGE_LENGTH = 10000;

const message = ref('');
const inputRef = ref<HTMLTextAreaElement | null>(null);
const triggerButtonRef = ref<HTMLButtonElement | null>(null);

const { isSupported, isListening, transcript, start: startSpeech, stop: stopSpeech } = useSpeechRecognition();

// When true, ignore the next transcript update (e.g. after send discards in-progress speech)
const discardTranscript = ref(false);

const toggleSpeechRecognition = (): void => {
  isListening.value ? stopSpeech() : startSpeech();
};

watch(transcript, (newText) => {
  if (discardTranscript.value) {
    discardTranscript.value = false;
    return;
  }
  if (newText) {
    message.value = message.value ? `${message.value} ${newText}` : newText;
    nextTick(() => adjustHeight());
  }
});

const remainingChars = computed(() => {
  return MAX_MESSAGE_LENGTH - message.value.length;
});

const isTooLong = computed(() => {
  return message.value.length > MAX_MESSAGE_LENGTH;
});

const canSend = computed(() => {
  return (
    message.value.trim().length > 0 && message.value.length <= MAX_MESSAGE_LENGTH && !props.disabled && !props.isLoading
  );
});

const handleSend = (): void => {
  if (!canSend.value) return;

  // Stop mic and discard any in-progress speech before sending
  if (isListening.value) {
    discardTranscript.value = true;
    stopSpeech();
  }

  emit('send', message.value.trim());
  message.value = '';

  nextTick(() => {
    if (inputRef.value) {
      inputRef.value.style.height = 'auto';
    }
  });
};

const handleInput = (): void => {
  const textarea = inputRef.value;
  if (textarea && textarea.value.length > MAX_MESSAGE_LENGTH) {
    message.value = textarea.value.slice(0, MAX_MESSAGE_LENGTH);
  }
  adjustHeight();
};

const adjustHeight = (): void => {
  const textarea = inputRef.value;
  if (textarea) {
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 128)}px`;
  }
};

const focus = (): void => {
  inputRef.value?.focus();
};

const handleToggleTenantSelector = (): void => {
  emit('toggle-tenant-selector');
};

defineExpose({ focus, triggerButtonRef });
</script>
